// ─── Tiefenachse des Sternenhimmels ─────────────────────────────────────────
//
// Die Achse ordnet von alt (hinten) nach jung (vorn). Wonach dabei sortiert
// wird, ist keine Kleinigkeit: nach Geburtsjahr zu sortieren klingt
// naheliegend, scheitert aber an der Praxis – in den meisten Stammbäumen fehlt
// bei einem guten Teil der Personen das Datum, und die landeten dann allesamt
// an derselben Stelle mitten in einer fremden Generation.
//
// Die Generationstiefe selbst steht in @genealogy-app/shared: die Chronik
// braucht sie ebenso für ihre Kapitel.

import { berechneGenerationen, median } from '@genealogy-app/shared';

export { berechneGenerationen, median };

// ─── Die Tiefenachse ─────────────────────────────────────────────────────────

export type Sortierung = 'generation' | 'jahr';

/** Abstand zweier Generationen auf der Tiefenachse. */
export const GENERATIONS_ABSTAND = 70;

export interface TiefenEingabe {
  id: string;
  /** Geburtsjahr, soweit gepflegt. */
  jahr: number | null;
}

export interface Tiefenlage {
  /** Die Z-Koordinate: negativ = alt (hinten), positiv = jung (vorn). */
  z: number;
  /** Das verwendete Jahr – bei fehlender Angabe geschätzt. */
  jahr: number;
  /** Ob dieses Jahr geschätzt ist. */
  geschaetzt: boolean;
}

/**
 * Legt fest, wo jede Person auf der Tiefenachse sitzt.
 *
 * Zwei Eigenschaften sind wichtiger als die genauen Zahlen:
 *
 *   1. In der Generations-Sortierung liegt ein Kind IMMER vor seinen Eltern –
 *      auch wenn kein einziges Geburtsdatum gepflegt ist.
 *   2. Beide Sortierungen nehmen dieselbe Gesamttiefe ein. Bei festem Maßstab
 *      je Jahr wären hundert Jahre ein Vielfaches dessen, was vier
 *      Generationen einnehmen, und der Baum verkäme zu einem dünnen Faden.
 *      Was die Jahres-Sortierung beiträgt, sind ohnehin nicht die absoluten
 *      Abstände, sondern die ungleichen – dass zwischen zwei Generationen mal
 *      zwanzig und mal vierzig Jahre liegen.
 */
export function tiefenAchse(
  personen: TiefenEingabe[],
  generation: Map<string, number>,
  modus: Sortierung,
): Map<string, Tiefenlage> {
  const lagen = new Map<string, Tiefenlage>();
  if (personen.length === 0) return lagen;

  const tiefe = (id: string) => generation.get(id) ?? 0;
  const maxGen = personen.reduce((m, p) => Math.max(m, tiefe(p.id)), 0);
  const mitteGen = maxGen / 2;

  // Wo liegt eine Generation typischerweise? Wer kein Geburtsdatum hat,
  // bekommt den Median SEINER Generation statt des Durchschnitts aller
  // Personen – sonst landeten alle Unbekannten mitten in einer fremden
  // Generation und der Baum sähe verknotet aus.
  const jahreJeGen = new Map<number, number[]>();
  for (const p of personen) {
    if (p.jahr == null) continue;
    const g = tiefe(p.id);
    if (!jahreJeGen.has(g)) jahreJeGen.set(g, []);
    jahreJeGen.get(g)!.push(p.jahr);
  }
  const medianJeGen = new Map<number, number>();
  for (const [g, js] of jahreJeGen) {
    const m = median(js);
    if (m != null) medianJeGen.set(g, m);
  }
  const basisJahr = median([...jahreJeGen.values()].flat()) ?? 1950;
  // Ohne einen einzigen Jahrgang in der Generation: 30 Jahre je Generation,
  // gerechnet ab der Mitte.
  const jahrVon = (p: TiefenEingabe): number =>
    p.jahr ?? medianJeGen.get(tiefe(p.id)) ?? Math.round(basisJahr + (tiefe(p.id) - mitteGen) * 30);

  const jahre = personen.map(jahrVon);
  const minJahr = Math.min(...jahre);
  const maxJahr = Math.max(...jahre);
  const mittelJahr = (minJahr + maxJahr) / 2;
  const gesamtTiefe = Math.max(maxGen, 1) * GENERATIONS_ABSTAND;
  const jahrSkala = maxJahr > minJahr ? gesamtTiefe / (maxJahr - minJahr) : 0;

  personen.forEach((p, i) => {
    const jahr = jahre[i];
    lagen.set(p.id, {
      z:
        modus === 'generation'
          ? (tiefe(p.id) - mitteGen) * GENERATIONS_ABSTAND
          : (jahr - mittelJahr) * jahrSkala,
      jahr,
      geschaetzt: p.jahr == null,
    });
  });

  return lagen;
}
